import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "countDisplay"]
  static values = { 
    debounceDelay: { type: Number, default: 300 },
    endpoint: { type: String, default: "/api/companies_search/count" }
  }

  connect() {    
    // 初期化
    this.abortController = null
    this.debounceTimer = null
    this.requestCounter = 0
    this.currentAnimation = null  // アニメーション管理
    
    // グローバル関数として登録
    window.openSearchOverlay = (searchParams = {}) => this.openSearchOverlay(searchParams)
    
    // カスタムイベントリスナーを追加
    this.element.addEventListener('multi-select-changed', this.handleMultiSelectChange.bind(this))
    
    // フォーム要素にイベントリスナーを設定
    this.setupFormListeners()
    
    // 件数はオーバーレイを開いた時のみ取得（ページロード時の不要なAPI呼び出しを防止）
  }

  disconnect() {
    if (this.abortController) {
      this.abortController.abort()
    }
    clearTimeout(this.debounceTimer)
    if (this.currentAnimation) {
      clearInterval(this.currentAnimation)
    }
  }

  setupFormListeners() {
    if (!this.hasFormTarget) return

    const form = this.formTarget

    // キーワード入力
    const keywordField = form.querySelector('input[name="keyword"]')
    if (keywordField) {
      keywordField.addEventListener('input', () => this.updateCountKeyword())
    }

    // 各セレクトフィールド
    ['target', 'area', 'job_type'].forEach(fieldName => {
      const field = form.querySelector(`select[name="${fieldName}"]`)
      if (field) {
        field.addEventListener('change', (e) => {
          this.updateCountImmediate()
        })
      }
    })

    // 複数選択フィールド（こだわり条件など）
    const multiSelectFields = form.querySelectorAll('select[multiple], input[type="checkbox"][name="point[]"], input[type="checkbox"][name^="point"]')
    multiSelectFields.forEach(field => {
      field.addEventListener('change', (e) => {
        this.updateCountImmediate()
      })
    })

    // 全てのinput[name="point[]"]とselect[name="point[]"]要素を対象
    const pointFields = form.querySelectorAll('input[name="point[]"], select[name="point[]"], input[name^="point"], select[name^="point"]')
    pointFields.forEach(field => {
      field.addEventListener('change', (e) => {
        this.updateCountImmediate()
      })
    })

    // フォーム送信
    form.addEventListener('submit', () => {
      if (typeof closeSearchOverlay === 'function') {
        closeSearchOverlay()
      }
    })
  }

  handleMultiSelectChange(event) {
    this.updateCountImmediate()
  }

  updateCount() {
    this.scheduleUpdate(this.debounceDelayValue)
  }

  updateCountKeyword() {
    this.scheduleUpdate(this.debounceDelayValue + 200)
  }

  updateCountImmediate() {
    this.scheduleUpdate(100)
  }

  scheduleUpdate(delay) {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.performCountUpdate(), delay)
  }

  async performCountUpdate() {
    const requestId = ++this.requestCounter
    
    try {
      if (!this.hasFormTarget) {
        return
      }
      
    } catch (earlyError) {
      this.updateCountDisplay(0)
      return
    }
    
    try {

      // 前回のリクエストをキャンセル
      if (this.abortController) {
        this.abortController.abort()
      }
      this.abortController = new AbortController()

      // フォームデータを取得
      const formData = new FormData(this.formTarget)
      const params = new URLSearchParams()
      
      // デバッグ: FormDataの内容をログ出力
      for (let [key, value] of formData.entries()) {
      }
      
      // 単一値のパラメータを処理
      for (let [key, value] of formData.entries()) {
        if (value !== '') {
          params.append(key, value)
        }
      }

      // デバッグ: 送信されるパラメータをログ出力
      for (let [key, value] of params.entries()) {
      }
      
      
      // API呼び出し
      const url = `${this.endpointValue}?${params}`
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        signal: this.abortController.signal
      })
      
      
      if (response.ok) {
        try {
          const data = await response.json()
          
          // デュアルカウント表示に対応
          if (data.recruit_count !== undefined && data.company_count !== undefined) {
            this.updateDualCount(data.recruit_count, data.company_count)
          } else {
            // 従来の単一カウント
            this.updateCountDisplay(data.count || data.recruit_count || 0)
          }
        } catch (jsonError) {
          this.updateCountDisplay(0)
        }
        
      } else {
        this.updateCountDisplay(0)
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.updateCountDisplay(0)
      }
    }
  }

  // 🎨 アニメーション復活版
  updateCountDisplay(count) {
    
    if (!this.hasCountDisplayTarget) {
      return
    }

    const countElement = this.countDisplayTarget
    const currentCount = parseInt(countElement.textContent) || 0
    
    
    // recruit_count の更新要素も検索
    const recruitCountElement = document.querySelector("#recruit-count");
    const companyCountElement = document.querySelector("#company-count");
    
    
    // 全ての recruit-count, company-count 要素を検索
    const allRecruitElements = document.querySelectorAll("#recruit-count, .recruit-count, [data-recruit-count]");
    const allCompanyElements = document.querySelectorAll("#company-count, .company-count, [data-company-count]");
    
    
    allRecruitElements.forEach((el, i) => {
    });
    
    allCompanyElements.forEach((el, i) => {
    });
    
    // 既存のアニメーションを停止
    if (this.currentAnimation) {
      clearInterval(this.currentAnimation)
      this.currentAnimation = null
    }
    
    if (currentCount !== count) {
      // 値が変わる場合はアニメーション
      this.animateCountSmoothly(currentCount, count)
    } else {
      // 値が同じ場合は視覚的フィードバックのみ
      countElement.textContent = count
      this.showUpdateFeedback()
    }
  }

  // デュアルカウント用の新しいメソッド
  updateDualCount(recruitCount, companyCount) {
    
    // 既存のデュアルカウント要素をチェック
    let recruitElement = document.querySelector("#recruit-count");
    let companyElement = document.querySelector("#company-count");
    
    if (recruitElement && companyElement) {
      // 既にデュアルカウント表示が存在する場合はアニメーション付きで更新
      const currentRecruitCount = parseInt(recruitElement.textContent) || 0;
      const currentCompanyCount = parseInt(companyElement.textContent) || 0;
      
      // アニメーション付きで更新
      this.animateCountElement(recruitElement, currentRecruitCount, recruitCount);
      this.animateCountElement(companyElement, currentCompanyCount, companyCount);
      return;
    }
    
    // 初回作成の場合
    if (this.hasCountDisplayTarget) {
      const countDisplay = this.countDisplayTarget;
      const parent = countDisplay.parentNode;
      
      
      // デュアルカウント用のHTML構造を作成
      const dualCountHTML = `
        <div class="dual-count-display">
          <div class="font-medium text-blue-600">
            <span id="recruit-count">${recruitCount}</span>件の求人が見つかりました
          </div>
          <div class="text-xs text-gray-500 mt-1">
            <span id="company-count">${companyCount}</span>件の企業が見つかりました
          </div>
        </div>
      `;
      
      // 既存の表示をデュアルカウントに置き換え
      parent.innerHTML = dualCountHTML;
      
      
      // 新しく作成された要素のスタイルを設定
      const newRecruitElement = document.querySelector("#recruit-count");
      const newCompanyElement = document.querySelector("#company-count");
      
      if (newRecruitElement) {
        newRecruitElement.style.color = '#2563eb'; // blue-600
      }
      
      if (newCompanyElement) {
        newCompanyElement.style.color = '#6b7280'; // gray-500
      }
      
    } else {
    }
    
    // 従来の処理は実行しない（既に新しい表示に置き換えたため）
  }

  // 個別要素のカウントアニメーション
  animateCountElement(element, from, to) {
    const duration = 600; // アニメーション時間を少し長めに
    const steps = 30;
    const stepValue = (to - from) / steps;
    const stepDuration = duration / steps;
    
    let currentStep = 0;
    
    // 元のスタイルを保存
    const originalTransform = element.style.transform;
    const originalTransition = element.style.transition;
    
    // アニメーション開始時の視覚効果（より強力に適用）
    element.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
    element.style.transform = 'scale(1.4)'; // さらに大きく
    element.style.transformOrigin = 'center';
    element.style.willChange = 'transform'; // GPUアクセラレーションを有効化
    
    
    const animation = setInterval(() => {
      currentStep++;
      const currentValue = Math.round(from + (stepValue * currentStep));
      
      element.textContent = currentValue;
      
      if (currentStep >= steps) {
        clearInterval(animation);
        
        // 最終値を確実に設定
        element.textContent = to;
        
        // 元のスタイルに戻す
        setTimeout(() => {
          element.style.transform = originalTransform || 'scale(1)';
          setTimeout(() => {
            element.style.transition = originalTransition;
            element.style.willChange = 'auto';
          }, 300);
        }, 100);
      }
    }, stepDuration);
  }

  // 🎨 スムーズなアニメーション
  animateCountSmoothly(from, to) {
    if (!this.hasCountDisplayTarget) return
    
    
    const duration = 400
    const steps = 20
    const stepValue = (to - from) / steps
    const stepDuration = duration / steps
    
    let currentStep = 0
    
    // 開始時にフィードバック
    this.showUpdateFeedback()
    
    this.currentAnimation = setInterval(() => {
      currentStep++
      const currentValue = Math.round(from + (stepValue * currentStep))
      
      if (this.hasCountDisplayTarget) {
        this.countDisplayTarget.textContent = currentValue
      }
      
      if (currentStep >= steps) {
        clearInterval(this.currentAnimation)
        this.currentAnimation = null
        
        // 最終値を確実に設定
        if (this.hasCountDisplayTarget) {
          this.countDisplayTarget.textContent = to
        }
      }
    }, stepDuration)
  }

  // ✨ 視覚的フィードバック
  showUpdateFeedback() {
    const container = this.countDisplayTarget?.parentElement
    if (!container) return

    // パルスエフェクト
    container.style.transform = 'scale(1.05)'
    container.style.transition = 'all 0.2s ease'
    container.style.backgroundColor = '#dbeafe'
    
    setTimeout(() => {
      container.style.transform = 'scale(1)'
      container.style.backgroundColor = ''
    }, 200)
  }

  // 🎨 アニメーション（改良版）
  animateCount(from, to) {
    this.animateCountSmoothly(from, to)
  }

  setLoadingState(isLoading) {
    // シンプルなローディング表示（必要に応じて実装）
  }

  // グローバル関数として呼び出される検索オーバーレイのオープン機能
  openSearchOverlay(searchParams = {}) {
    // 検索パラメータがある場合はフォームに設定
    if (Object.keys(searchParams).length > 0) {
      this.setFormValues(searchParams)
    } else {
      // オーバーレイを開いたタイミングで件数を取得（ページロード時ではなくここで）
      this.updateCount()
    }

    // オーバーレイを表示
    this.element.classList.remove('hidden')
    
    // パネルをスライドイン
    const panel = this.element.querySelector('#search-panel')
    if (panel) {
      requestAnimationFrame(() => {
        panel.style.transform = "translateX(0%)"
      })
    }
    
    // bodyのスクロールを防ぐ
    document.body.style.overflow = 'hidden'
  }

  setFormValues(searchParams) {
    const form = this.element.querySelector('form')
    if (!form) {
      return
    }


    // 各フィールドに値を設定
    Object.keys(searchParams).forEach(key => {
      const value = searchParams[key]
      if (!value) {
        return
      }

      const field = form.querySelector(`[name="${key}"]`)
      
      if (field) {
        if (field.type === 'checkbox' || field.type === 'radio') {
          field.checked = true
        } else {
          field.value = value
        }
      } else {
        // すべてのフィールドを表示してデバッグ
        const allFields = form.querySelectorAll('input, select, textarea')
      }
    })

    // こだわり条件（複数選択）の処理
    if (searchParams.point && Array.isArray(searchParams.point)) {
      searchParams.point.forEach(pointValue => {
        const checkbox = form.querySelector(`input[name="point[]"][value="${pointValue}"]`)
        if (checkbox) {
          checkbox.checked = true
        }
      })
      
      // マルチセレクトドロップダウンの表示更新
      const multiSelectElement = form.querySelector('[data-controller*="multi-select-dropdown"]')
      if (multiSelectElement) {
        const multiSelectController = this.application.getControllerForElementAndIdentifier(
          multiSelectElement,
          'multi-select-dropdown'
        )
        if (multiSelectController && multiSelectController.updateButtonText) {
          multiSelectController.updateButtonText()
        }
      }
    }

    // フォーム値設定後に件数を更新
    this.updateCount()
  }
}